import { useQuery } from '@tanstack/react-query';
import api from '../services/api';

// Cache times in milliseconds
const ANALYTICS_CACHE_TIME = 10 * 60 * 1000; // 10 minutes
const COMPANIES_CACHE_TIME = 10 * 60 * 1000; // 10 minutes
const COMPARISON_CACHE_TIME = 10 * 60 * 1000; // 10 minutes

/**
 * Hook to fetch analytics overview data with caching
 * @param {string} [databaseId] - Optional database ID for multi-database support
 */
export function useAnalyticsOverview(databaseId) {
  return useQuery({
    queryKey: ['analytics', 'overview', databaseId || 'legacy'],
    queryFn: async () => {
      const params = databaseId ? { databaseId } : {};
      const response = await api.get('/analytics/overview', { params });
      return response.data;
    },
    staleTime: ANALYTICS_CACHE_TIME,
    cacheTime: ANALYTICS_CACHE_TIME * 2,
  });
}

/**
 * Hook to fetch industry analytics data with caching
 * @param {string} [databaseId] - Optional database ID
 */
export function useIndustryAnalytics(databaseId) {
  return useQuery({
    queryKey: ['analytics', 'industry', databaseId || 'legacy'],
    queryFn: async () => {
      const params = databaseId ? { databaseId } : {};
      const response = await api.get('/analytics/by-industry', { params });
      return response.data;
    },
    staleTime: ANALYTICS_CACHE_TIME,
    cacheTime: ANALYTICS_CACHE_TIME * 2,
  });
}

/**
 * Hook to fetch region analytics data with caching
 * @param {string} [databaseId] - Optional database ID
 */
export function useRegionAnalytics(databaseId) {
  return useQuery({
    queryKey: ['analytics', 'region', databaseId || 'legacy'],
    queryFn: async () => {
      const params = databaseId ? { databaseId } : {};
      const response = await api.get('/analytics/by-region', { params });
      return response.data;
    },
    staleTime: ANALYTICS_CACHE_TIME,
    cacheTime: ANALYTICS_CACHE_TIME * 2,
  });
}

/**
 * Hook to fetch village analytics data with caching
 * @param {string} [databaseId] - Optional database ID
 */
export function useVillageAnalytics(databaseId) {
  return useQuery({
    queryKey: ['analytics', 'village', databaseId || 'legacy'],
    queryFn: async () => {
      const params = databaseId ? { databaseId } : {};
      const response = await api.get('/analytics/by-village', { params });
      return response.data;
    },
    staleTime: ANALYTICS_CACHE_TIME,
    cacheTime: ANALYTICS_CACHE_TIME * 2,
  });
}

/**
 * Hook to fetch sector analytics (Government vs Non-Government) with caching
 * @param {string} [databaseId] - Optional database ID
 */
export function useSectorAnalytics(databaseId) {
  return useQuery({
    queryKey: ['analytics', 'sector', databaseId || 'legacy'],
    queryFn: async () => {
      const params = databaseId ? { databaseId } : {};
      const response = await api.get('/analytics/by-sector', { params });
      return response.data;
    },
    staleTime: ANALYTICS_CACHE_TIME,
    cacheTime: ANALYTICS_CACHE_TIME * 2,
  });
}

/**
 * Hook to fetch ministry analytics with caching
 * @param {string} [databaseId] - Optional database ID
 */
export function useMinistryAnalytics(databaseId) {
  return useQuery({
    queryKey: ['analytics', 'ministry', databaseId || 'legacy'],
    queryFn: async () => {
      const params = databaseId ? { databaseId } : {};
      const response = await api.get('/analytics/by-ministry', { params });
      return response.data;
    },
    staleTime: ANALYTICS_CACHE_TIME,
    cacheTime: ANALYTICS_CACHE_TIME * 2,
  });
}

/**
 * Hook to fetch all companies with caching
 * @param {Object} params - Query parameters (limit, industry, region, databaseId)
 */
export function useCompanies(params = {}) {
  const { limit = 200, industry, region, databaseId } = params;

  return useQuery({
    queryKey: ['companies', { limit, industry, region, databaseId: databaseId || 'legacy' }],
    queryFn: async () => {
      const queryParams = { limit, industry, region };
      if (databaseId) queryParams.databaseId = databaseId;
      const response = await api.get('/companies', { params: queryParams });
      return response.data;
    },
    staleTime: COMPANIES_CACHE_TIME,
    cacheTime: COMPANIES_CACHE_TIME * 2,
  });
}

/**
 * Hook to fetch company comparison data with caching
 * @param {string} companyId - Company ID to compare
 * @param {string} filterType - Type of filter: 'all', 'industry', 'village', 'region', 'sector', 'ministry'
 * @param {Object} options - Additional React Query options (may include databaseId)
 */
export function useCompanyComparison(companyId, filterType = 'all', options = {}) {
  const { databaseId, ...queryOptions } = options;
  return useQuery({
    queryKey: ['comparison', companyId, filterType, databaseId || 'legacy'],
    queryFn: async () => {
      const params = { filterType };
      if (databaseId) params.databaseId = databaseId;
      const response = await api.get(`/analytics/compare/${companyId}`, { params });
      return response.data;
    },
    staleTime: COMPARISON_CACHE_TIME,
    cacheTime: COMPARISON_CACHE_TIME * 2,
    enabled: !!companyId,
    ...queryOptions,
  });
}

/**
 * Hook to fetch all comparison data types simultaneously
 * @param {string} companyId - Company ID to compare
 * @param {string} [databaseId] - Optional database ID
 */
export function useCompanyComparisons(companyId, databaseId) {
  const opts = { databaseId, enabled: !!companyId };

  const allCompaniesQuery = useCompanyComparison(companyId, 'all', opts);
  const industryQuery = useCompanyComparison(companyId, 'industry', opts);
  const villageQuery = useCompanyComparison(companyId, 'village', opts);
  const regionQuery = useCompanyComparison(companyId, 'region', opts);
  const sectorQuery = useCompanyComparison(companyId, 'sector', opts);
  const ministryQuery = useCompanyComparison(companyId, 'ministry', opts);

  return {
    allCompaniesComparison: allCompaniesQuery.data,
    industryComparison: industryQuery.data,
    villageComparison: villageQuery.data,
    regionComparison: regionQuery.data,
    sectorComparison: sectorQuery.data,
    ministryComparison: ministryQuery.data,
    isLoading: allCompaniesQuery.isLoading || industryQuery.isLoading ||
               villageQuery.isLoading || regionQuery.isLoading ||
               sectorQuery.isLoading || ministryQuery.isLoading,
    isError: allCompaniesQuery.isError || industryQuery.isError ||
             villageQuery.isError || regionQuery.isError ||
             sectorQuery.isError || ministryQuery.isError,
    error: allCompaniesQuery.error || industryQuery.error ||
           villageQuery.error || regionQuery.error ||
           sectorQuery.error || ministryQuery.error,
  };
}

/**
 * Hook to fetch category breakdown data with caching
 * @param {string} categoryType - Type of category
 * @param {string} categoryValue - Specific category value
 * @param {string} [databaseId] - Optional database ID
 */
export function useBreakdown(categoryType, categoryValue, databaseId) {
  return useQuery({
    queryKey: ['breakdown', categoryType, categoryValue, databaseId || 'legacy'],
    queryFn: async () => {
      const params = databaseId ? { databaseId } : {};
      const response = await api.get(
        `/analytics/breakdown/${categoryType}/${encodeURIComponent(categoryValue)}`,
        { params }
      );
      return response.data;
    },
    staleTime: ANALYTICS_CACHE_TIME,
    cacheTime: ANALYTICS_CACHE_TIME * 2,
    enabled: !!categoryValue,
  });
}

/**
 * Hook to fetch paired (pre/post) responses for a company in a paired database
 * @param {string} companyId
 * @param {string} databaseId
 */
export function useCompanyPairedResponses(companyId, databaseId) {
  return useQuery({
    queryKey: ['paired-responses', companyId, databaseId],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/paired-responses`, {
        params: { databaseId },
      });
      return response.data;
    },
    staleTime: ANALYTICS_CACHE_TIME,
    cacheTime: ANALYTICS_CACHE_TIME * 2,
    enabled: !!companyId && !!databaseId,
  });
}

/**
 * Hook to fetch MaxDiff aggregation for a configured chart on a database
 * @param {string} databaseId
 * @param {string} chartKey - key from dashboardLayout.maxDiffCharts
 * @param {string} [companyId] - optional, scope to one company
 */
export function useMaxDiff(databaseId, chartKey, companyId) {
  return useQuery({
    queryKey: ['max-diff', databaseId, chartKey, companyId || 'all'],
    queryFn: async () => {
      const params = { chartKey };
      if (companyId) params.companyId = companyId;
      const response = await api.get(`/databases/${databaseId}/max-diff`, { params });
      return response.data;
    },
    staleTime: ANALYTICS_CACHE_TIME,
    cacheTime: ANALYTICS_CACHE_TIME * 2,
    enabled: !!databaseId && !!chartKey,
  });
}

/**
 * Hook to fetch time series analytics data with caching
 * @param {Object} params - Query parameters (binSize, industry, village, region, sector, ministry, databaseId)
 */
export function useTimeSeriesAnalytics(params = {}) {
  const { binSize = 'day', industry, village, region, sector, ministry, databaseId } = params;

  return useQuery({
    queryKey: ['analytics', 'timeSeries', { binSize, industry, village, region, sector, ministry, databaseId: databaseId || 'legacy' }],
    queryFn: async () => {
      const queryParams = { binSize, industry, village, region, sector, ministry };
      if (databaseId) queryParams.databaseId = databaseId;
      const response = await api.get('/analytics/time-series', { params: queryParams });
      return response.data;
    },
    staleTime: ANALYTICS_CACHE_TIME,
    cacheTime: ANALYTICS_CACHE_TIME * 2,
  });
}
